import type { PopDiagLogEvent, PopDiagnosticLogLevel, PopEncoderStats, PopSignalingAPI } from 'common/types/pop-signaling';

const LEVEL_MAP: Record<string, number> = { off: 0, info: 1, debug: 2, verbose: 3 };

export interface PopDiagLogObserverContext {
  level: PopDiagnosticLogLevel;
  wasSummary: boolean;
}

export type PopDiagLogObserver = (event: PopDiagLogEvent, context: PopDiagLogObserverContext) => void;

let diagLogObserver: PopDiagLogObserver | undefined;
let currentDiagnosticLevel: PopDiagnosticLogLevel = 'off';

export const setPopH264DiagLogObserver = (observer: PopDiagLogObserver | undefined) => {
  diagLogObserver = observer;
};

export const getPopH264DiagnosticLevel = () => currentDiagnosticLevel;

const notifyDiagLogObserver = (event: PopDiagLogEvent, context: PopDiagLogObserverContext) => {
  try {
    diagLogObserver?.(event, context);
  } catch (_error) {
    // Diagnostics must never break native callback delivery.
  }
};

/** Parsed C++ per-encoder diagnostic data (emitted every 1s) */
interface CppEncoderDiag {
  framesEncoded: number;
  framesIn: number;
  userActivity: number;
  screenActivity: number;
  encodeActivity: number;
  targetFps: number;
  skips: {
    pacer: number;
    budget: number;
    pause: number;
    decoder: number;
    debounce: number;
    kf: number;
  };
  timestamp: number;
}

/** Latest C++ diag data per encoder ID */
const cppDiagByEncoder = new Map<number, CppEncoderDiag>();

const DIAG_SUMMARY_RE =
  /PopH264Diag\[1s\]: encoded=(\d+)\/(\d+) user=([\d.]+) screen=([\d.]+) encode=([\d.]+)(?: .*?)? target_fps=([\d.]+) \| skips: pacer=(\d+) budget=(\d+) pause=(\d+) decoder=(\d+) debounce=(\d+) kf=(\d+)/;

function parseCppDiagMessage(encoderId: number, message: string): boolean {
  const m = DIAG_SUMMARY_RE.exec(message);
  if (!m) return false;
  cppDiagByEncoder.set(encoderId, {
    framesEncoded: parseInt(m[1]),
    framesIn: parseInt(m[2]),
    userActivity: parseFloat(m[3]),
    screenActivity: parseFloat(m[4]),
    encodeActivity: parseFloat(m[5]),
    targetFps: parseFloat(m[6]),
    skips: {
      pacer: parseInt(m[7]),
      budget: parseInt(m[8]),
      pause: parseInt(m[9]),
      decoder: parseInt(m[10]),
      debounce: parseInt(m[11]),
      kf: parseInt(m[12]),
    },
    timestamp: Date.now(),
  });
  return true;
}

export function formatPopDiagLogLineForConsole(event: PopDiagLogEvent): string {
  if (event.message.startsWith('[pop-recv]')) {
    return event.message;
  }
  return `[PopH264:${event.encoderId}] ${event.message}`;
}

// Track all RTCPeerConnections for WebRTC stats collection
const trackedPcs = new Set<WeakRef<RTCPeerConnection>>();
let pcsPatched = false;

function trackPc(pc: RTCPeerConnection) {
  for (const ref of trackedPcs) {
    if (ref.deref() === pc) return; // already tracked
  }
  trackedPcs.add(new WeakRef(pc));
}

/** Find existing PeerConnections from Pop's connection registry */
function discoverExistingPeerConnections() {
  const g = window as any;
  const connections = g.globals?.connections;
  if (!connections) return;
  for (const sessionId of Object.keys(connections)) {
    const conn = connections[sessionId];
    const mc = conn?.activeConnection?.mediaConnection;
    if (!mc) continue;
    for (const mgr of Object.values(mc.mediaConnectionManagers || {})) {
      const p2pConns = (mgr as any)?.p2pMediaConnections;
      if (!p2pConns) continue;
      for (const peerId of Object.keys(p2pConns)) {
        const peerConn = p2pConns[peerId];
        for (const dir of ['sendConnection', 'receiveConnection']) {
          const pc = peerConn?.[dir]?.connection;
          if (pc instanceof RTCPeerConnection) trackPc(pc);
        }
      }
    }
  }
}

function patchRTCPeerConnection() {
  if (pcsPatched) return;
  pcsPatched = true;
  const Orig = window.RTCPeerConnection;
  const origProto = Orig.prototype;
  window.RTCPeerConnection = function (this: RTCPeerConnection, ...args: any[]) {
    const pc = new Orig(...args);
    trackPc(pc);
    return pc;
  } as any;
  window.RTCPeerConnection.prototype = origProto;
  for (const key of Object.getOwnPropertyNames(Orig)) {
    if (key !== 'prototype' && key !== 'length' && key !== 'name') {
      try {
        (window.RTCPeerConnection as any)[key] = (Orig as any)[key];
      } catch {}
    }
  }
}

function getActivePeerConnections(): RTCPeerConnection[] {
  // Always re-discover in case new connections appeared
  discoverExistingPeerConnections();
  const alive: RTCPeerConnection[] = [];
  for (const ref of trackedPcs) {
    const pc = ref.deref();
    if (!pc || pc.connectionState === 'closed') {
      trackedPcs.delete(ref);
      continue;
    }
    alive.push(pc);
  }
  return alive;
}

interface WebRTCSnapshot {
  bytesSent: number;
  framesEncoded: number;
  framesSent: number;
  timestamp: number;
  qualityLimitationReason: string;
  encoderImplementation: string;
  targetBitrate: number;
  availableOutgoingBitrate: number;
  videoTrackCount: number;
  activeVideoTracks: number;
}

let prevSnapshot: WebRTCSnapshot | null = null;

async function collectWebRTCStats(): Promise<{
  bandwidthMbps: number;
  encodedFps: number;
  sentFps: number;
  qualityLimitationReason: string;
  encoderImplementation: string;
  targetBitrateMbps: number;
  availableBandwidthMbps: number;
  peerConnectionCount: number;
  videoTrackCount: number;
  activeVideoTracks: number;
} | null> {
  const pcs = getActivePeerConnections();
  if (pcs.length === 0) return null;

  let bytesSent = 0;
  let framesEncoded = 0;
  let framesSent = 0;
  let timestamp = 0;
  let qualityLimitationReason = 'none';
  let encoderImplementation = '';
  let targetBitrate = 0;
  let availableOutgoingBitrate = 0;
  let videoTrackCount = 0;
  let activeVideoTracks = 0;

  for (const pc of pcs) {
    try {
      const stats = await pc.getStats();
      stats.forEach((report: any) => {
        if (report.type === 'outbound-rtp' && report.kind === 'video') {
          bytesSent += report.bytesSent ?? 0;
          framesEncoded += report.framesEncoded ?? 0;
          framesSent += report.framesSent ?? 0;
          timestamp = report.timestamp;
          if (report.qualityLimitationReason) qualityLimitationReason = report.qualityLimitationReason;
          if (report.encoderImplementation) encoderImplementation = report.encoderImplementation;
        }
        if (report.type === 'candidate-pair' && report.nominated) {
          if (report.availableOutgoingBitrate) availableOutgoingBitrate = report.availableOutgoingBitrate;
        }
      });
      for (const sender of pc.getSenders()) {
        if (sender.track?.kind === 'video') {
          videoTrackCount++;
          if (sender.track.enabled && sender.track.readyState === 'live') activeVideoTracks++;
          const params = sender.getParameters() as RTCRtpParameters & {
            encodings?: RTCRtpEncodingParameters[];
          };
          for (const enc of params.encodings ?? []) {
            if (enc.maxBitrate) targetBitrate = Math.max(targetBitrate, enc.maxBitrate);
          }
        }
      }
    } catch {}
  }

  const snap: WebRTCSnapshot = {
    bytesSent,
    framesEncoded,
    framesSent,
    timestamp: timestamp || Date.now(),
    qualityLimitationReason,
    encoderImplementation,
    targetBitrate,
    availableOutgoingBitrate,
    videoTrackCount,
    activeVideoTracks,
  };

  if (!prevSnapshot) {
    prevSnapshot = snap;
    return null;
  }

  const dt = (snap.timestamp - prevSnapshot.timestamp) / 1000;
  if (dt <= 0) return null;

  const bandwidthMbps = ((snap.bytesSent - prevSnapshot.bytesSent) * 8) / dt / 1_000_000;
  const encodedFps = (snap.framesEncoded - prevSnapshot.framesEncoded) / dt;
  const sentFps = (snap.framesSent - prevSnapshot.framesSent) / dt;

  prevSnapshot = snap;

  return {
    bandwidthMbps,
    encodedFps,
    sentFps,
    qualityLimitationReason: snap.qualityLimitationReason,
    encoderImplementation: snap.encoderImplementation,
    targetBitrateMbps: snap.targetBitrate / 1_000_000,
    availableBandwidthMbps: snap.availableOutgoingBitrate / 1_000_000,
    peerConnectionCount: pcs.length,
    videoTrackCount: snap.videoTrackCount,
    activeVideoTracks: snap.activeVideoTracks,
  };
}

function formatEncoderStats(id: number, s: PopEncoderStats): string {
  const gateStatus = s.qpGate > 0 ? 'OPEN' : 'LOCKED';
  const activityPct = (s.encodeActivity * 100).toFixed(0);
  const cpuUsagePct = ((s.cpuUsage ?? 0) * 100).toFixed(0);
  const cpuThrottlePct = ((s.cpuThrottle ?? 1) * 100).toFixed(0);
  return [
    `  Encoder #${id} ${s.width}x${s.height}`,
    `    QP: ${s.qp} avg=${s.qpAvg >= 0 ? s.qpAvg.toFixed(1) : '-'} target=${s.targetQp.toFixed(1)}  gate=${s.qpGate.toFixed(3)} (${gateStatus})`,
    `    Activity: ${activityPct}%  Target FPS: ${s.framerate.toFixed(1)}  Bitrate: ${s.bitrateKbps} kbps  Ideal: ${s.idealKbits.toFixed(0)} kbits  CPU: ${cpuUsagePct}%/${cpuThrottlePct}%`,
  ].join('\n');
}

type EncoderState = 'ok' | 'idle' | 'converging' | 'capture-limited' | 'skipping' | 'gate-locked' | 'unknown';

interface FramerateExplanation {
  fps: number;
  state: EncoderState;
  text: string;
}

function explainFramerate(encoderId: number, s: PopEncoderStats): FramerateExplanation {
  const cpp = cppDiagByEncoder.get(encoderId);

  // If no C++ data yet, give a basic explanation
  if (!cpp) {
    if (s.encodeActivity < 0.1) return { fps: s.framerate, state: 'idle', text: `IDLE — activity=${(s.encodeActivity * 100).toFixed(0)}%, target=${s.framerate.toFixed(0)}fps` };
    if (s.framerate >= 28) return { fps: s.framerate, state: 'ok', text: 'OK' };
    return { fps: s.framerate, state: 'unknown', text: `target=${s.framerate.toFixed(0)}fps (waiting for C++ diag data...)` };
  }

  const actual = cpp.framesEncoded;
  const target = cpp.targetFps;
  const totalSkips = cpp.skips.pacer + cpp.skips.budget + cpp.skips.pause + cpp.skips.decoder + cpp.skips.debounce + cpp.skips.kf;

  if (actual >= 28) return { fps: actual, state: 'ok', text: `OK — ${actual}fps` };

  const parts: string[] = [];
  let state: EncoderState = 'unknown';

  // 1. Low activity → low target FPS (the adaptive pacer is intentionally throttling)
  // Combined activity matches the C++ ComputeTargetEncodeFps/ComputeIdealFrameKbits
  // signal: max of all three activity channels.
  const combinedActivity = Math.max(cpp.encodeActivity, cpp.screenActivity, cpp.userActivity);
  if (target < 28 && combinedActivity < 0.5) {
    state = 'idle';
    parts.push(`activity low (encode=${(cpp.encodeActivity * 100).toFixed(0)}%, screen=${(cpp.screenActivity * 100).toFixed(0)}%, user=${(cpp.userActivity * 100).toFixed(0)}%) → target only ${target.toFixed(0)}fps`);
  } else if (target < 28) {
    state = 'converging';
    parts.push(`target converging: ${target.toFixed(1)}fps (activity: encode=${(cpp.encodeActivity * 100).toFixed(0)}%, screen=${(cpp.screenActivity * 100).toFixed(0)}%)`);
  }

  // 2. Input-limited: encoder wanted more frames than it received
  if (target >= 20 && actual < target * 0.8 && totalSkips === 0) {
    state = 'capture-limited';
    parts.push(`capture-limited: only ${actual} input frames/s vs target ${target.toFixed(0)} (0 skips — bottleneck is upstream of encoder)`);
  }

  // 3. Skip reasons (when frames were actively dropped)
  if (totalSkips > 0) {
    state = 'skipping';
    const skipParts: string[] = [];
    if (cpp.skips.pacer > 0) skipParts.push(`pacer=${cpp.skips.pacer}`);
    if (cpp.skips.budget > 0) skipParts.push(`budget=${cpp.skips.budget}`);
    if (cpp.skips.pause > 0) skipParts.push(`pause=${cpp.skips.pause}`);
    if (cpp.skips.decoder > 0) skipParts.push(`decoder_not_ready=${cpp.skips.decoder}`);
    if (cpp.skips.debounce > 0) skipParts.push(`resolution_debounce=${cpp.skips.debounce}`);
    if (cpp.skips.kf > 0) skipParts.push(`keyframe_throttle=${cpp.skips.kf}`);
    parts.push(`${totalSkips} frames skipped: ${skipParts.join(', ')}`);
  }

  // 4. Gate locked (QP gate preventing encoding)
  if (s.qpGate === 0) {
    state = 'gate-locked';
    parts.push(`QP gate LOCKED (qp=${s.qp} avg=${s.qpAvg.toFixed(1)} target=${s.targetQp.toFixed(1)}) — quality too low, pausing to save bandwidth`);
  }

  if (parts.length === 0) {
    parts.push(`${actual}fps encoded, target=${target.toFixed(0)}fps`);
  }

  return { fps: actual, state, text: `${actual}fps — ` + parts.join('; ') };
}

let diagInterval: ReturnType<typeof setInterval> | null = null;

async function emitDashboard(ps: PopSignalingAPI) {
  const encoderIds = ps.getEncoderIds();
  const webrtc = await collectWebRTCStats();

  if (!webrtc) {
    const pcs = getActivePeerConnections();
    if (pcs.length === 0) {
      console.log('[PopH264 Diag] No PeerConnections found — waiting for call to connect...');
    } else {
      console.log(`[PopH264 Diag] ${pcs.length} PeerConnection(s) found, collecting first sample...`);
    }
    return;
  }

  const lines: string[] = ['--- PopH264 Diagnostics ---'];

  // Connection status
  lines.push(
    `  Connections: ${webrtc.peerConnectionCount} PC(s), ${webrtc.videoTrackCount} video track(s)` +
      ` (${webrtc.activeVideoTracks} active)`,
  );

  // Bandwidth line
  lines.push(
    `  Bandwidth: ${webrtc.bandwidthMbps.toFixed(1)} Mbps actual` +
      (webrtc.availableBandwidthMbps > 0 ? ` | BWE: ${webrtc.availableBandwidthMbps.toFixed(1)} Mbps` : '') +
      (webrtc.targetBitrateMbps > 0 ? ` | Max: ${webrtc.targetBitrateMbps.toFixed(0)} Mbps` : ''),
  );

  // FPS line
  lines.push(
    `  FPS: encoded=${webrtc.encodedFps.toFixed(1)} sent=${webrtc.sentFps.toFixed(1)}` +
      `  Encoder: ${webrtc.encoderImplementation || 'none'}` +
      `  QualityLimit: ${webrtc.qualityLimitationReason}`,
  );

  // No active video explanation
  if (webrtc.activeVideoTracks === 0 && encoderIds.length === 0) {
    lines.push('  ⚠ No active video — screen share not started or no video tracks sending');
  }

  // WebRTC-level quality limitation
  if (webrtc.qualityLimitationReason === 'bandwidth') {
    lines.push(`  WebRTC: quality limited by BANDWIDTH (BWE=${webrtc.availableBandwidthMbps.toFixed(1)} Mbps, max=${webrtc.targetBitrateMbps.toFixed(0)} Mbps)`);
  } else if (webrtc.qualityLimitationReason === 'cpu') {
    lines.push('  WebRTC: quality limited by CPU');
  }

  // Per-encoder stats with C++ data
  const explanations: { id: number; ex: FramerateExplanation }[] = [];
  for (const id of encoderIds) {
    const s = ps.getEncoderStats?.(id);
    if (!s) continue;
    const ex = explainFramerate(id, s);
    explanations.push({ id, ex });
    lines.push(formatEncoderStats(id, s));
    lines.push('  ' + ex.text);
  }

  // Summary: min framerate tile and why
  if (explanations.length > 0) {
    const min = explanations.reduce((a, b) => (a.ex.fps < b.ex.fps ? a : b));
    lines.push(`  >> Bottleneck: tile #${min.id} at ${min.ex.fps}fps [${min.ex.state}]`);
  }

  lines.push('---');
  console.log(lines.join('\n'));
}

export const popDiagnostics = (level: PopDiagnosticLogLevel) => {
  const ps = (window as any)?.electron?.popSignaling as PopSignalingAPI | undefined;
  if (!ps) {
    console.warn('popDiagnostics: popSignaling not available (not running in Electron?)');
    return;
  }

  const numLevel = LEVEL_MAP[level] ?? 0;
  currentDiagnosticLevel = LEVEL_MAP[level] === undefined ? 'off' : level;

  // Wire up C++ → JS diagnostic log callback — parse structured summaries, log the rest
  ps.setDiagLogCallback((e) => {
    const wasSummary = parseCppDiagMessage(e.encoderId, e.message);
    notifyDiagLogObserver(e, { level: currentDiagnosticLevel, wasSummary });
    if (!wasSummary) {
      // Log non-summary messages (skip events, receive diagnostics, etc.) directly
      console.log(formatPopDiagLogLineForConsole(e));
    }
  });

  // Set level on all encoders
  const ids = ps.getEncoderIds();
  ids.forEach((id) => ps.setDiagnosticLogLevel(id, numLevel));

  // Clear previous dashboard interval
  if (diagInterval) {
    clearInterval(diagInterval);
    diagInterval = null;
    prevSnapshot = null;
  }

  if (numLevel === 0) {
    cppDiagByEncoder.clear();
    console.log('PopH264 diagnostics OFF');
    return;
  }

  // Discover existing PeerConnections + patch for future ones
  discoverExistingPeerConnections();
  patchRTCPeerConnection();

  const pcs = getActivePeerConnections();
  console.log(`PopH264 diagnostics: ${level} (level ${numLevel}) — ${ids.length} encoder(s), ${pcs.length} PeerConnection(s)`);
  if (ps.getEncoderStats && ids.length > 0) {
    const s = ps.getEncoderStats(ids[0]);
    if (s) console.log(formatEncoderStats(ids[0], s));
  }

  // Emit rich dashboard every second
  diagInterval = setInterval(() => void emitDashboard(ps), 1000);
};
